import React, { useCallback, useEffect, useRef, useState } from "react";
import useFetch from "hooks/useFetch";
import {
  Alert,
  Button,
  Card,
  Icon,
  styles as ui,
  useConfirm,
} from "stories/Admin";
import {
  describeSourceCount,
  isTashkeelRunning,
  TANZIL_URL,
  TashkeelReport,
  TashkeelSyncStatus,
  tashkeelProgressPercent,
  tashkeelStatusSummary,
} from "./tashkeelSync";
import styles from "./QuranPage.module.scss";

const POLL_MS = 2000;

interface Props {
  /** Kalles når en synk er ferdig, så valgt surah kan lastes på nytt. */
  onFinished?: () => void;
}

/**
 * «Hent arabisk med tashkeel»: lagrer Tanzil «quran-simple» (full tashkeel,
 * vanlig skrivemåte for Naskh-fonter som Amiri) i arabicTashkeel. Kjører i
 * bakgrunnen på serveren; panelet viser fremdrift og oppsummering.
 */
const TashkeelSyncPanel: React.FC<Props> = ({ onFinished }) => {
  const { execute } = useFetch();
  const confirm = useConfirm();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const [report, setReport] = useState<TashkeelReport | null>(null);
  const [status, setStatus] = useState<TashkeelSyncStatus | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const fetchReport = useCallback(async () => {
    const result = (await executeRef.current(
      "/api/v1/admin/quran/tashkeel/report",
      "GET",
    )) as TashkeelReport | null;
    if (result) setReport(result);
  }, []);

  const fetchStatus = useCallback(async () => {
    const result = (await executeRef.current(
      "/api/v1/admin/quran/tashkeel/sync",
      "GET",
    )) as TashkeelSyncStatus | null;
    if (result) setStatus(result);
    return result;
  }, []);

  useEffect(() => {
    fetchReport();
    fetchStatus();
  }, [fetchReport, fetchStatus]);

  const running = isTashkeelRunning(status);
  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(async () => {
      const next = await fetchStatus();
      if (next && !isTashkeelRunning(next)) {
        await fetchReport();
        onFinishedRef.current?.();
      }
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [running, fetchStatus, fetchReport]);

  const start = async (overwrite: boolean) => {
    if (
      !(await confirm({
        title: overwrite
          ? "Hente arabisk med tashkeel på nytt for alle vers?"
          : "Hente arabisk med tashkeel?",
        message: overwrite
          ? "Teksten fra Tanzil erstatter arabisk med tashkeel i alle vers, også der den er redigert. Arabisk uten tashkeel og IndoPak-teksten endres ikke."
          : "Arabisk med full tashkeel (Tanzil «quran-simple», vanlig skrivemåte for Amiri/Scheherazade) hentes for alle vers som mangler den. Hvert vers kontrolleres mot dagens arabiske tekst før det lagres. Eksisterende tekst endres ikke. Det tar et par minutter.",
        confirmLabel: "Hent arabisk med tashkeel",
      }))
    ) {
      return;
    }
    setStarting(true);
    setError(null);
    const result = (await executeRef.current(
      `/api/v1/admin/quran/tashkeel/sync${overwrite ? "?overwrite=true" : ""}`,
      "POST",
    )) as TashkeelSyncStatus | null;
    if (result) {
      setStatus(result);
      if (!isTashkeelRunning(result)) {
        await fetchReport();
        onFinishedRef.current?.();
      }
    } else {
      setError("Kunne ikke starte hentingen av arabisk med tashkeel.");
    }
    setStarting(false);
  };

  const showResult =
    status && (status.state === "done" || status.state === "failed");
  const missing = report?.missingAyahs ?? 0;

  return (
    <Card
      title="Arabisk med tashkeel"
      subtitle={
        report
          ? missing === 0
            ? `Alle ${report.totalAyahs.toLocaleString("nb-NO")} vers har arabisk med tashkeel.`
            : `${report.withTashkeel.toLocaleString("nb-NO")} av ${report.totalAyahs.toLocaleString("nb-NO")} vers har arabisk med tashkeel (${missing.toLocaleString("nb-NO")} mangler).`
          : "Sjekker …"
      }
      actions={
        <>
          {report && missing === 0 && (
            <Button
              variant="secondary"
              onClick={() => start(true)}
              disabled={running || starting}
            >
              Hent på nytt
            </Button>
          )}
          <Button
            onClick={() => start(false)}
            disabled={running || starting}
            loading={running || starting}
            icon={<Icon name="download" size={16} strokeWidth={2} />}
          >
            {running ? "Henter tashkeel …" : "Hent arabisk med tashkeel"}
          </Button>
        </>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}

      {status && running && (
        <div className={styles.syncProgress} aria-live="polite">
          <div
            className={styles.syncBar}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={tashkeelProgressPercent(status)}
          >
            <span style={{ width: `${tashkeelProgressPercent(status)}%` }} />
          </div>
          <span className={ui.help}>{tashkeelStatusSummary(status)}</span>
        </div>
      )}

      {showResult && status && (
        <Alert
          tone={
            status.state === "failed"
              ? "error"
              : status.mismatches.length || status.failedSurahs.length
                ? "warn"
                : "ok"
          }
        >
          {tashkeelStatusSummary(status)}
        </Alert>
      )}

      {showResult && status && status.sourceAyahs > 0 && (
        <span className={ui.help}>
          {describeSourceCount(status)}{" "}
          {status.offsetSurahs.map((s) => (
            <span key={s.surahNumber}>
              Surah {s.surahNumber} {s.name}: {s.note}.{" "}
            </span>
          ))}
        </span>
      )}

      {showResult &&
        status &&
        (status.mismatches.length > 0 ||
          status.failedSurahs.length > 0 ||
          status.missingInSource.length > 0) && (
          <div className={styles.syncList}>
            <button
              type="button"
              className={ui.help}
              onClick={() => setShowDetails((v) => !v)}
            >
              {showDetails ? "Skjul" : "Vis"} avvik
            </button>
            {showDetails && (
              <ul>
                {status.failedSurahs.map((s) => (
                  <li key={`f${s.surahNumber}`}>
                    Surah {s.surahNumber} {s.name} feilet: {s.message}
                  </li>
                ))}
                {status.mismatches.map((m) => (
                  <li key={`m${m.surahNumber}:${m.ayahNumber}`}>
                    {m.surahNumber}:{m.ayahNumber} ble ikke lagret: {m.reason}
                    {m.similarity != null ? ` (likhet ${m.similarity})` : ""}
                  </li>
                ))}
                {status.missingInSource.map((ref) => (
                  <li key={`s${ref}`}>{ref} finnes ikke hos kilden</li>
                ))}
              </ul>
            )}
          </div>
        )}

      <span className={ui.help}>
        Velg «Naskh» som arabisk font i surah-visningen for å se teksten.
        Kilde: Tanzil Quran Text (Simple), ©{" "}
        <a href={TANZIL_URL} target="_blank" rel="noreferrer">
          Tanzil Project – tanzil.net
        </a>{" "}
        (CC BY 3.0). Kilden må vises der teksten brukes, og teksten skal ikke
        endres.
      </span>
    </Card>
  );
};

export default TashkeelSyncPanel;
